import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosApi } from "../../helpers/AxiosApi";
import { toast } from "react-toastify";
import initialStates from "./state";

// get Card List
export const getCardList = createAsyncThunk(
  "getCardList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.get(
        `cards?sort_by=is_default&sort_direction=desc`
      );
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Add Card
export const addCard = createAsyncThunk(
  "addCard",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.post(`cards`, data);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
// Update Card
export const updateCard = createAsyncThunk(
  "updateCard",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.put(`cards/${id}`, data);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete Card
export const deleteCard = createAsyncThunk(
  "deleteCard",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.delete(`cards/${id}`);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// get Billing Subscriptions
export const getBillingSubscriptions = createAsyncThunk(
  "getBillingSubscriptions",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.get(`billing-subscriptions/${id}`);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
// get Subscriptions Details
export const getSubscriptionDetails = createAsyncThunk(
  "getSubscriptionDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.get(`my-subscription/${id}`);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Add Billing Information
export const addBillingInformation = createAsyncThunk(
  "addBillingInformation",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.post(`billing-informations`, data);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Cancel Renewal Subscription
export const cancelRenewalSubscription = createAsyncThunk(
  "cancelRenewalSubscription",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.delete(
        `cancel-renewal-subscription/${id}`
      );
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// Auto Renewal Subscription
export const autoRenewalSubscription = createAsyncThunk(
  "autoRenewalSubscription",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.patch(`auto-renewal-subscription/${id}`);
      toast.success(response.data.message);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

// get billing transactions Details
export const getBillingTransactionsDetails = createAsyncThunk(
  "getBillingTransactionsDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await AxiosApi.get(`billing-transactions/${id}`);
      return response.data;
    } catch (error) {
      if (!error) {
        throw error.response;
      }
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

const billingSubscriptionsSlice = createSlice({
  name: "billingSubscriptions",
  initialState: initialStates,
  extraReducers: (builder) => {
    builder
      // Upload File
      .addCase(getCardList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCardList.fulfilled, (state, action) => {
        state.loading = false;
        state.getCardListStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getCardList.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Add Card
      .addCase(addCard.pending, (state) => {
        state.loading = true;
      })
      .addCase(addCard.fulfilled, (state, action) => {
        state.loading = false;
        state.addCardStatus.data = action.payload;
        state.error = false;
      })
      .addCase(addCard.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Update Card
      .addCase(updateCard.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCard.fulfilled, (state, action) => {
        state.loading = false;
        state.updateCardStatus.data = action.payload;
        state.error = false;
      })
      .addCase(updateCard.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Delete Card
      .addCase(deleteCard.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteCardStatus.data = action.payload;
        state.error = false;
      })
      .addCase(deleteCard.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // get Billing Subscriptions
      .addCase(getBillingSubscriptions.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBillingSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.getBillingSubscriptionsStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getBillingSubscriptions.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // get Subscription Details
      .addCase(getSubscriptionDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubscriptionDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.getSubscriptionDetailsStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getSubscriptionDetails.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Add Billing Information
      .addCase(addBillingInformation.pending, (state) => {
        state.loading = true;
      })
      .addCase(addBillingInformation.fulfilled, (state, action) => {
        state.loading = false;
        state.addBillingInformationStatus.data = action.payload;
        state.error = false;
      })
      .addCase(addBillingInformation.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Cancel Renewal Subscription
      .addCase(cancelRenewalSubscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelRenewalSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.cancelRenewalSubscriptionStatus.data = action.payload;
        state.error = false;
      })
      .addCase(cancelRenewalSubscription.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // Auto Renewal Subscription
      .addCase(autoRenewalSubscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(autoRenewalSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.autoRenewalSubscriptionStatus.data = action.payload;
        state.error = false;
      })
      .addCase(autoRenewalSubscription.rejected, (state) => {
        state.loading = false;
        state.error = true;
      })
      // get Billing Transactions Details
      .addCase(getBillingTransactionsDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getBillingTransactionsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.getBillingTransactionsDetailsStatus.data = action.payload;
        state.error = false;
      })
      .addCase(getBillingTransactionsDetails.rejected, (state) => {
        state.loading = false;
        state.error = true;
      });
  },
});

const { reducer } = billingSubscriptionsSlice;
export default reducer;
